import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowBackRounded,
  CheckCircleRounded,
  CancelRounded,
  QuizRounded,
} from '@mui/icons-material';
import {
  generateConversationQuiz,
  getConversationQuiz,
  getConversationQuizEligibility,
  submitConversationQuiz,
} from '../services/api';
import { ConversationQuiz, QuizQuestionResult } from '../types';

type PageState = 'loading' | 'generating' | 'ready' | 'submitting' | 'results' | 'failed' | 'error';

const QuizPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const convId = Number(conversationId);

  const [pageState, setPageState] = useState<PageState>('loading');
  const [quiz, setQuiz] = useState<ConversationQuiz | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [results, setResults] = useState<QuizQuestionResult[] | null>(null);
  const [score, setScore] = useState<number | null>(null);
  const [correctCount, setCorrectCount] = useState<number | null>(null);
  const [totalQuestions, setTotalQuestions] = useState<number | null>(null);
  const [topicCompleted, setTopicCompleted] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Cancellable in-flight generate. If the user clicks Try again twice, or
  // navigates away mid-regen, the previous request is aborted before a new
  // one starts. The backend's _generate_full_quiz commits its state regardless
  // (race on the final write), but the client never displays the stale result.
  const generateAbortRef = useRef<AbortController | null>(null);

  const loadOrGenerate = useCallback(async (regenerate: boolean) => {
    if (Number.isNaN(convId)) {
      setError('Invalid conversation id.');
      setPageState('error');
      return;
    }

    // Cancel any previous regenerate before kicking off a new one.
    if (generateAbortRef.current) {
      generateAbortRef.current.abort();
    }
    const controller = new AbortController();
    generateAbortRef.current = controller;

    setError(null);
    setPageState(regenerate ? 'generating' : 'loading');

    try {
      let data: ConversationQuiz;
      if (regenerate) {
        data = await generateConversationQuiz(convId, true, controller.signal);
      } else {
        try {
          const eligibility = await getConversationQuizEligibility(convId, controller.signal);
          if (eligibility.has_new_content) {
            setPageState('generating');
            data = await generateConversationQuiz(convId, false, controller.signal);
          } else {
            data = await getConversationQuiz(convId, controller.signal);
          }
        } catch (err) {
          if (err instanceof Error && err.message === 'QUIZ_NOT_FOUND') {
            setPageState('generating');
            data = await generateConversationQuiz(convId, false, controller.signal);
          } else {
            throw err;
          }
        }
      }

      setQuiz(data);
      if (data.status === 'completed' && data.results) {
        setResults(data.results);
        setScore(data.score ?? null);
        setCorrectCount(data.correct_count ?? null);
        setTotalQuestions(data.total_questions ?? null);
        setPageState('results');
      } else if (data.status === 'generating') {
        // Another client (or this one in a prior tab) is mid-regen. The
        // transactional refactor preserves the prior questions on disk during
        // 'generating', but they're stale to us — show the spinner instead of
        // letting the user answer them. (See backend to_quiz_response: prior
        // data is also hidden at the API layer for defence in depth.)
        setAnswers({});
        setResults(null);
        setScore(null);
        setCorrectCount(null);
        setTotalQuestions(null);
        setPageState('generating');
      } else if (data.status === 'failed') {
        // Backend marked this row failed after an LLM regen error. Don't try
        // to render an empty quiz — show a recovery card instead. See
        // backend/src/quizzes/service.py:_generate_full_quiz except branch.
        setAnswers({});
        setResults(null);
        setScore(null);
        setCorrectCount(null);
        setTotalQuestions(null);
        setPageState('failed');
      } else {
        setAnswers({});
        setResults(null);
        setScore(null);
        setCorrectCount(null);
        setTotalQuestions(null);
        setPageState('ready');
      }
    } catch (err: any) {
      // User-cancelled requests aren't errors — swallow silently so the UI
      // doesn't flash an error card when a newer request supersedes us.
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      // If we just tried to regenerate, the backend row is now status='failed'
      // (see backend/src/quizzes/service.py:_generate_full_quiz except branch).
      // Land back on the friendly retry card instead of the generic error
      // screen — keeps the UX symmetric for the same failure mode.
      setPageState(regenerate ? 'failed' : 'error');
    }
  }, [convId]);

  // Abort any in-flight regenerate when QuizPage unmounts (user navigated away).
  useEffect(() => {
    return () => {
      if (generateAbortRef.current) {
        generateAbortRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    loadOrGenerate(false);
  }, [loadOrGenerate]);

  const allAnswered = useMemo(() => {
    if (!quiz?.questions?.length) return false;
    return quiz.questions.every((q) => answers[q.id]);
  }, [quiz, answers]);

  const handleSubmit = async () => {
    if (!allAnswered || pageState !== 'ready') return;
    setPageState('submitting');
    setError(null);
    try {
      const response = await submitConversationQuiz(convId, answers);
      setResults(response.results);
      setScore(response.score);
      setCorrectCount(response.correct_count);
      setTotalQuestions(response.total_questions);
      setTopicCompleted(response.topic_completed ?? null);
      setPageState('results');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit quiz.');
      setPageState('ready');
    }
  };

  const handleRetake = async () => {
    await loadOrGenerate(true);
  };

  if (pageState === 'loading' || pageState === 'generating') {
    return (
      <div className="main-gradient-bg min-h-screen flex items-center justify-center px-4">
        <div className="rounded-3xl border border-violet-200 bg-white/95 p-8 text-center shadow-sm max-w-md w-full">
          <QuizRounded className="mx-auto mb-4 text-violet-600" fontSize="large" />
          <p className="text-sm text-slate-600">
            {pageState === 'generating'
              ? 'Building your quiz from this conversation…'
              : 'Loading quiz…'}
          </p>
        </div>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="main-gradient-bg min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>
        <button
          type="button"
          onClick={() => navigate('/chat')}
          className="rounded-xl bg-violet-500 px-4 py-2 text-sm font-medium text-white hover:bg-violet-600"
        >
          Back to chat
        </button>
      </div>
    );
  }

  if (pageState === 'failed') {
    // Row exists but the last LLM generation failed (status='failed'). Surface
    // a clear retry instead of an empty quiz card. Re-calling loadOrGenerate
    // with regenerate=true POSTs to /generate?regenerate=true on the backend
    // which re-enters _generate_full_quiz on the same row.
    return (
      <div className="main-gradient-bg min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <div className="max-w-md w-full rounded-3xl border border-amber-200 bg-white/95 p-7 shadow-sm text-center">
          <QuizRounded className="mx-auto mb-3 text-amber-600" fontSize="large" />
          <h1 className="text-lg font-bold text-slate-900 mb-1">Couldn't build the quiz</h1>
          <p className="text-sm text-slate-600 mb-4">
            Something went wrong on our end while making questions from this conversation.
            Try again — usually it works on the next attempt.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/chat')}
              className="rounded-xl border border-violet-200 bg-white px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-50"
            >
              Back to chat
            </button>
            <button
              type="button"
              onClick={handleRetake}
              className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-gradient-bg min-h-screen px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto w-full max-w-3xl space-y-6">
        <header className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/chat')}
            className="flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-violet-700"
          >
            <ArrowBackRounded fontSize="small" /> Back to chat
          </button>
        </header>

        <section className="rounded-3xl border border-violet-200 bg-white/95 p-6 shadow-sm space-y-2">
          <div className="flex items-center gap-2">
            <QuizRounded className="text-violet-600" />
            <h1 className="text-xl font-bold text-slate-900">{quiz?.title || 'Conversation Quiz'}</h1>
          </div>
          {pageState === 'results' && score !== null && (
            <p className="text-sm text-slate-600">
              You scored <span className="font-semibold text-violet-700">{score}%</span>
              {correctCount != null && totalQuestions != null && (
                <> ({correctCount}/{totalQuestions} correct)</>
              )}
            </p>
          )}
          {pageState === 'results' && topicCompleted && (
            <p className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800">
              🎉 Topic complete — “{topicCompleted}” is now marked done for you!
            </p>
          )}
        </section>

        {error && (
          <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>
        )}

        {pageState === 'results' && results ? (
          <section className="space-y-4">
            {results.map((result) => (
              <article
                key={result.question_id}
                className={`rounded-2xl border p-5 shadow-sm ${
                  result.correct ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'
                }`}
              >
                <div className="flex items-start gap-2">
                  {result.correct ? (
                    <CheckCircleRounded className="text-green-600 mt-0.5" fontSize="small" />
                  ) : (
                    <CancelRounded className="text-red-600 mt-0.5" fontSize="small" />
                  )}
                  <div className="flex-1">
                    <h2 className="font-semibold text-slate-800">{result.question}</h2>
                    <p className="mt-2 text-sm text-slate-600">{result.explanation}</p>
                  </div>
                </div>
              </article>
            ))}
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="button"
                onClick={() => navigate('/chat')}
                className="rounded-xl border border-violet-200 bg-white px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-50"
              >
                Back to chat
              </button>
              <button
                type="button"
                onClick={handleRetake}
                className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Start fresh quiz
              </button>
            </div>
          </section>
        ) : (
          <section className="space-y-4">
            {quiz?.questions.map((question, idx) => (
              <article
                key={question.id}
                className="rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-sm"
              >
                <h2 className="font-semibold text-slate-800">
                  {idx + 1}. {question.question}
                </h2>
                <div className="mt-3 space-y-2">
                  {question.options.map((option) => {
                    const selected = answers[question.id] === option.id;
                    return (
                      <label
                        key={option.id}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 text-sm transition-colors ${
                          selected
                            ? 'border-violet-400 bg-violet-50 text-violet-900'
                            : 'border-slate-200 hover:border-violet-200'
                        }`}
                      >
                        <input
                          type="radio"
                          name={question.id}
                          value={option.id}
                          checked={selected}
                          onChange={() =>
                            setAnswers((prev) => ({
                              ...prev,
                              [question.id]: option.id,
                            }))
                          }
                          className="text-violet-600"
                        />
                        <span>{option.text}</span>
                      </label>
                    );
                  })}
                </div>
              </article>
            ))}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!allAnswered || pageState === 'submitting'}
              className="w-full rounded-xl bg-indigo-600 px-4 py-3 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {pageState === 'submitting' ? 'Submitting…' : 'Submit answers'}
            </button>
          </section>
        )}
      </div>
    </div>
  );
};

export default QuizPage;
